import React, { useContext, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { Menu, MenuItem } from "@material-ui/core";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import QueueOverrideRequestAuthorizationModal from "../QueueOverrideRequestAuthorizationModal";
import ReopenTicketModal from "../ReopenTicketModal";
import toastError from "../../errors/toastError";
import validateLongAwaitingTimeTickets from "../../utils/validateLongAwaitingTimeTickets";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const TicketOptionsContextMenu = ({
  ticket, ticketsList, timeAwaitingFlag, awaitingTimeSetting, transferTicketModalOpen,
  editTicketModalOpen, menuOpen, handleClose, anchorEl, setTabOpen, setPropertiesModalOpen
}) => {
  //  ****************
  //  ** Variables  **
  //  ****************
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [openReopenTicketModal, setOpenReopenTicketModal] = useState(false);
  const [openQueueOverrideRequestAuthorizationModal, setOpenQueueOverrideRequestAuthorizationModal] = useState(false);

  const {
    getSetting
    , setContactViewContactId
    , setContactViewWhatsappId
    , queueOverrideAuthorizers
    , setAreThereQueueOverrideRequests
    , setShouldSyncRouteOnMenu
  } = useGlobalRegisters();



  //  ***************
  //  ** Functions **
  //  ***************
  const handleOpenReopenTicketModal = () => {
    setOpenReopenTicketModal(true);
  };

  const handleCloseReopenTicketModal = () => {
    setOpenReopenTicketModal(false);
    handleClose();
  };

  const handleOpenQueueOverrideRequestAuthorizationModal = () => {
    setOpenQueueOverrideRequestAuthorizationModal(true);
  };

  const handleCloseQueueOverrideRequestAuthorizationModal = () => {
    setOpenQueueOverrideRequestAuthorizationModal(false);
  };

  const validateForceAcceptLongAwaitingTimeTickets = (ticketsList, awaitingTimeSetting, considerOnlyWhatsappTickets) => {
    const forceAcceptLongAwaitingTimeTickets = getSetting("forceAcceptLongAwaitingTimeTickets");
      
    const shouldValidateLongAwaitingTimeTickets = forceAcceptLongAwaitingTimeTickets === "enabled"
      && user.forceAcceptLongAwaitingTimeTickets
      && !timeAwaitingFlag;

    return shouldValidateLongAwaitingTimeTickets
      ? validateLongAwaitingTimeTickets(ticketsList, awaitingTimeSetting, considerOnlyWhatsappTickets)
      : false;
  };

  const validateForceAcceptFirstTicket = async (currentTicketId, currentTicketType, considerOnlyWhatsappTickets) => {
		try {
			const forceAcceptFirstTicketSetting = getSetting("forceAcceptFirstTicket");
			if (forceAcceptFirstTicketSetting === "disabled") return { canSkipTicket: true };

			const { data } = await api.post("/queueOverrides/validation", {
				currentTicketId
        , currentTicketType
				, shouldConsiderOnlyWhatsappTickets: considerOnlyWhatsappTickets
				, queueOverrideAuthorizers
			});

			return data;
		}
		catch (exception) {
			console.log("validateForceAcceptFirstTicket exception:", exception);
			toast.error(exception);
			return { canSkipTicket: false, message: "backendErrors.ERR_FORCE_ACCEPT_FIRST_TICKET", shouldQueueOverrideBeAuthorized: false };
		}
  };

  const handleUpdateTicketStatus = async (status, userId) => {
    if (status === "open") {
      // ***---- Validation: Force Accept Long Awaiting Time Tickets ----***
      const areThereLongAwaitingTimeTickets = validateForceAcceptLongAwaitingTimeTickets(ticketsList, awaitingTimeSetting, true);

      if (areThereLongAwaitingTimeTickets) {
        toast.info(i18n.t("backendErrors.ERR_FORCE_ACCEPT_LONG_AWAITING_TIME_TICKETS"));
        handleClose();
        return;
      }

      // ***---- Validation: Force Accept First Ticket ----***
      const { canSkipTicket, message, shouldQueueOverrideBeAuthorized } = await validateForceAcceptFirstTicket(
        ticket.id
        , ticket.type
        , true
      );

      if (!canSkipTicket) {
        toast.info(i18n.t(message));

        if (shouldQueueOverrideBeAuthorized) handleOpenQueueOverrideRequestAuthorizationModal();
        handleClose();
        return;
      }

      // ***---- Processing: Update Ticket ----***
      try {
        await api.put(`/tickets/${ticket.id}/1`, {
          status,
          userId,
          directClosedById: null,
          isReturned: false,
          isTransfered: ticket.isTransfered,
          isBotInteractionFinished: true,
          updateLastActivityAt: true,
          contactId: ticket.contactId
        });

        DefaultFunctionsUtils.redirectToTicketsPage(history, `/${ticket.id}`);
        setShouldSyncRouteOnMenu(true);
        setTabOpen("open");
        localStorage.setItem("tabOpenTickets", "open");
      }
      catch (exception) {
        console.log("Handle Update Ticket Status Exception:", exception);
        toastError(exception);
      }
      finally {
        handleClose();
        api.get("/queueOverrides/exists").then(response => setAreThereQueueOverrideRequests(response.data));
      }
    }
    else {
      try {
        const isTicketBeingPending = status === "pending";
        const isTicketBeingClosed = status === "closed";

        await api.put(`/tickets/${ticket.id}/0`, {
          status,
          userId: userId && !isTicketBeingClosed ? userId : null,
          directClosedById: userId && isTicketBeingClosed ? userId : null,
          isReturned: isTicketBeingPending,
          isTransfered: !isTicketBeingPending ? ticket.isTransfered : false,
          isBotInteractionFinished: true,
          updateLastActivityAt: isTicketBeingPending,
          contactId: ticket.contactId
        });

        if (isTicketBeingPending) {
          setTabOpen(status);
          localStorage.setItem("tabOpenTickets", status);
        }

        DefaultFunctionsUtils.redirectToTicketsPage(history);
        setShouldSyncRouteOnMenu(true);
      }
      catch (exception) {
        console.log("Handle Update Ticket Status Exception:", exception);
        toastError(exception);
      }
      finally {
        handleClose();
        api.get("/queueOverrides/exists").then(response => setAreThereQueueOverrideRequests(response.data));
      }
    }
	};

  const handleViewTicket = (id, status) => {
    handleClose();
    
    if (ticket.type === 0) {
      setContactViewContactId(ticket.contactId);
      setContactViewWhatsappId(ticket.whatsappId);
    } else {
      setContactViewContactId(0);
      setContactViewWhatsappId(0);
    }

    if (["open", "pending", "group"].includes(status)) {
      DefaultFunctionsUtils.redirectToTicketsPage(history, `/${id}?tab=open`);
      setShouldSyncRouteOnMenu(true);
    }
		else if (status === "closed") {
      DefaultFunctionsUtils.redirectToTicketsPage(history, `/${id}?tab=closed`, 1);
      setShouldSyncRouteOnMenu(true);
    }
	};

  const handleTicketMarkStatus = async (id, isMarkedAsUnread) => {    
    handleClose();    
    if (isMarkedAsUnread) await handleMarkTicketAsRead(id)
    else await handleMarkTicketAsUnread(id);
  };

  const handleMarkTicketAsUnread = async (id) => {
    try {
      await api.put(`/ticketsSetAsUnread/${id}`, { ticketType: ticket.type });
    } catch (error) {
      console.log("Handle Mark Ticket As Unread Error:", error);
      toastError(error);
    }
  };

  const handleMarkTicketAsRead = async (id) => {
    try {
      await api.put(`/ticketsSetAsRead/${id}`, { ticketType: ticket.type });
    } catch(error) {
      console.log("Handle Mark Ticket As Read Error:", error);
      toastError(error);
    }
  };

  const handleTransfer = () => {
    handleClose();
    transferTicketModalOpen();
  };

  const handleEdit = () => {
    handleClose();
    editTicketModalOpen();
  };

  const handleOpenPropertiesModal = () => {
    handleClose();
    setPropertiesModalOpen(true);
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <>
      <ReopenTicketModal
        modalOpen={openReopenTicketModal}
        onClose={handleCloseReopenTicketModal}
        setTabOpen={setTabOpen}
        ticket={ticket}
      />

      {ticket.type === 0 && (
        <QueueOverrideRequestAuthorizationModal
          open={openQueueOverrideRequestAuthorizationModal}
          onClose={handleCloseQueueOverrideRequestAuthorizationModal}
          ticketId={ticket.id}
          contactId={ticket.contact.id}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        open={menuOpen}
        onClose={handleClose}
        onContextMenu={event => event.preventDefault()}
      >

        {/* ***---- Accept ----*** */}
        {ticket.status === "pending" && (
          <MenuItem onClick={async () => await handleUpdateTicketStatus("open", user?.id)}>
            {i18n.t("ticketOptionsContextMenu.accept")}
          </MenuItem>
        )}

        {/* ***---- View ----*** */}
        <MenuItem onClick={() => handleViewTicket(ticket.id, ticket.status)}>
          {i18n.t("ticketOptionsContextMenu.view")}
        </MenuItem>

        {/* ***---- Mark as Read/Unread ----*** */}
        {["pending", "open"].includes(ticket.status) && (
          <MenuItem
            onClick={async () => {
              const isTicketMarkedAsUnread = ticket.type === 3 && ticket.userIdInternalChat === user.id
                ? ticket.isMarkedAsUnreadInternalChat
                : ticket.isMarkedAsUnread;

              const isTicketWithUnreadMessages = ticket.unreadMessages > 0;
              await handleTicketMarkStatus(ticket.id, isTicketMarkedAsUnread || isTicketWithUnreadMessages);
          }}>
            {
              (ticket.type === 3 && ticket.userId === user.id && ticket.isMarkedAsUnread)
              || (ticket.type === 3 && ticket.userIdInternalChat === user.id && ticket.isMarkedAsUnreadInternalChat)
              || (ticket.type !== 3 && ticket.isMarkedAsUnread)
              || ticket.unreadMessages > 0 ? i18n.t("ticketOptionsContextMenu.markAsRead") : i18n.t("ticketOptionsContextMenu.markAsUnread")
            }
          </MenuItem>
        )}

        {/* ***---- Transfer ----*** */}
        {ticket.status === "open" && ticket.type !== 3 && (
          <MenuItem onClick={handleTransfer}>
            {i18n.t("ticketOptionsContextMenu.transfer")}
          </MenuItem>
        )}

        {/* ***---- Edit ----*** */}
        {["pending", "open"].includes(ticket.status) && [0, 1].includes(ticket.type) && (
          <MenuItem onClick={handleEdit}>
            {i18n.t("ticketOptionsContextMenu.edit")}
          </MenuItem>
        )}
        
        {/* ***---- Return ----*** */}
        {ticket.status === "open" && ticket.type !== 3 && (
          <MenuItem onClick={async () => await handleUpdateTicketStatus("pending", null)}>
            {i18n.t("ticketOptionsContextMenu.return")}
          </MenuItem>
        )}

        {/* ***---- Reopen ----*** */}
        {ticket.status === "closed" && ticket.type !== 3 && (
          <MenuItem onClick={handleOpenReopenTicketModal}>
            {i18n.t("ticketOptionsContextMenu.reopen")}
          </MenuItem>
        )}
        
        {/* ***---- Resolve (Pending) ----*** */}
        {ticket.status === "pending" && user.profile === "admin" && (
          <MenuItem onClick={async () => await handleUpdateTicketStatus("closed", user?.id)}>
            {i18n.t("ticketOptionsContextMenu.resolve")}
          </MenuItem>
        )}

        {/* ***---- Properties ----*** */}
        <MenuItem onClick={handleOpenPropertiesModal}>
          {i18n.t("ticketOptionsContextMenu.properties")}
        </MenuItem>

        {/* ***---- Close ----*** */}
        <MenuItem onClick={handleClose}>
          {i18n.t("ticketOptionsContextMenu.close")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default TicketOptionsContextMenu;
